<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Loader2, Send } from 'lucide-vue-next'

const props = defineProps<{
  isLoading?: boolean
}>()

const emit = defineEmits<{
  (e: 'submit', content: string, isInternal: boolean): void
}>()

const { t } = useI18n()
const authStore = useAuthStore()

const content = ref('')
const isInternal = ref(false)
const isSubmitting = ref(false)

const isAdmin = computed(() => authStore.user?.role === 'admin' || authStore.user?.role === 'operator')

const canSubmit = computed(() => content.value.trim().length > 0 && !isSubmitting.value && !props.isLoading)

const handleSubmit = async () => {
  if (!canSubmit.value) return

  isSubmitting.value = true
  try {
    emit('submit', content.value.trim(), isInternal.value)
    content.value = ''
    isInternal.value = false
  } finally {
    isSubmitting.value = false
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
    handleSubmit()
  }
}
</script>

<template>
  <div class="space-y-3">
    <Textarea
      v-model="content"
      :placeholder="t('comments.placeholder')"
      class="min-h-[80px] resize-none"
      :disabled="isSubmitting || isLoading"
      @keydown="handleKeydown"
    />

    <div class="flex items-center justify-between">
      <div v-if="isAdmin" class="flex items-center space-x-2">
        <Checkbox
          id="isInternal"
          v-model="isInternal"
          :disabled="isSubmitting || isLoading"
        />
        <Label for="isInternal" class="text-sm text-muted-foreground cursor-pointer">
          {{ t('comments.internalComment') }}
        </Label>
      </div>
      <div v-else></div>

      <Button
        @click="handleSubmit"
        :disabled="!canSubmit"
        size="sm"
      >
        <Loader2 v-if="isSubmitting" class="mr-2 h-4 w-4 animate-spin" />
        <Send v-else class="mr-2 h-4 w-4" />
        {{ t('comments.addComment') }}
      </Button>
    </div>

    <p class="text-xs text-muted-foreground">
      {{ t('comments.shortcut') }}
    </p>
  </div>
</template>
